// File: /src/components/ProjectTable.js

import React, { useState } from 'react';
import TableSortHeader from './TableSortHeader';
import { useSortableData } from '../hooks/useSortableData';
import '../styles/GridClients.css';

const ProjectTable = ({
	services,
	clients,
	openClientModal,
	selectedProjects,
	toggleSelectProject,
	isShowingArchived,
}) => {
	// Mapeamento para incluir o nome do cliente em cada projeto
	const servicesWithClient = React.useMemo(() => {
		return services.map((service) => {
			const client = clients.find((c) => c.id === service.clientId);
			return { ...service, clientName: client?.name || '-' };
		});
	}, [services, clients]);

	// Configuração inicial para ordenação dos projetos
	const initialConfig = { key: 'id', direction: 'asc' };

	// Hook para ordenação utilizando o array já mapeado
	const { sortedItems, sortConfig, handleSort } = useSortableData(
		servicesWithClient,
		initialConfig
	);

	// Para a coluna "Cliente", usamos a chave "clientName"
	const handleCustomSort = (key) => {
		if (key === 'client') {
			handleSort('clientName');
		} else {
			handleSort(key);
		}
	};

	const tableRowStyle = { cursor: 'pointer' };

	// Estado para controle de visibilidade da tabela (salvo no localStorage)
	const [showTable, setShowTable] = useState(() => {
		const savedState = localStorage.getItem('projectTableVisible');
		return savedState !== null ? JSON.parse(savedState) : true;
	});

	const toggleTableVisibility = () =>
		setShowTable((prev) => {
			const newState = !prev;
			localStorage.setItem(
				'projectTableVisible',
				JSON.stringify(newState)
			);
			return newState;
		});

	// Função para selecionar/desselecionar todos os projetos
	const handleSelectAll = (event) => {
		const allIds = sortedItems.map((service) => service.id);
		if (event.target.checked) {
			allIds.forEach((id) => {
				if (!selectedProjects.includes(id)) {
					toggleSelectProject(id);
				}
			});
		} else {
			allIds.forEach((id) => {
				if (selectedProjects.includes(id)) {
					toggleSelectProject(id);
				}
			});
		}
	};

	return (
		<div className="table-section">
			<h3 onClick={toggleTableVisibility} style={{ cursor: 'pointer' }}>
				Projetos {showTable ? '-' : '+'}
			</h3>
			{showTable && (
				<div className="table-container">
					<table>
						<thead>
							<tr>
								{/* Coluna para o checkbox de seleção geral */}
								<th>
									<input
										type="checkbox"
										className="grid-client-checkbox"
										checked={
											sortedItems.length > 0 &&
											sortedItems.every((service) =>
												selectedProjects.includes(
													service.id
												)
											)
										}
										onChange={handleSelectAll}
									/>
								</th>
								<TableSortHeader
									label="ID"
									sortKey="id"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Projeto"
									sortKey="name"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Descrição"
									sortKey="description"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Cliente"
									sortKey="client"
									currentSortConfig={{
										key:
											sortConfig.key === 'clientName'
												? 'client'
												: sortConfig.key,
										direction: sortConfig.direction,
									}}
									onSort={handleCustomSort}
								/>
								<TableSortHeader
									label="Valor"
									sortKey="value"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Data de Início"
									sortKey="start_date"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Data de Término"
									sortKey="end_date"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Status"
									sortKey="status"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
							</tr>
						</thead>
						<tbody>
							{sortedItems.length === 0 ? (
								<tr>
									<td colSpan="8">
										{isShowingArchived 
											? "Nenhum projeto arquivado."
											: "Nenhum projeto cadastrado."}
									</td>
								</tr>
							) : (
								sortedItems.map((service) => {
									const client = clients.find(
										(c) => c.id === service.clientId
									);
									return (
										<tr
											key={service.id}
											style={tableRowStyle}
											onClick={() =>
												client &&
												openClientModal(client)
											}
										>
											{/* Checkbox para seleção individual */}
											<td>
												<input
													type="checkbox"
													className="grid-client-checkbox"
													checked={selectedProjects.includes(
														service.id
													)}
													onClick={(e) =>
														e.stopPropagation()
													}
													onChange={() =>
														toggleSelectProject(
															service.id
														)
													}
												/>
											</td>
											<td>{service.id}</td>
											<td>{service.name}</td>
											<td>{service.description}</td>
											<td>{service.clientName}</td>
											<td>
												{Number(
													service.value
												).toLocaleString('pt-BR', {
													style: 'currency',
													currency: 'BRL',
												})}
											</td>
											<td>
												{new Date(service.start_date).toLocaleString('pt-BR', {
													dateStyle: 'short',
													timeStyle: 'short'
												})}
											</td>
											<td>
												{new Date(service.end_date).toLocaleString('pt-BR', {
													dateStyle: 'short',
													timeStyle: 'short'
												})}
											</td>
											<td>
												<span
													className={`status-badge ${
														service.status ===
														'pending'
															? 'status-pending'
															: service.status ===
															  'in_progress'
															? 'status-in-progress'
															: service.status ===
															  'completed'
															? 'status-completed'
															: ''
													}`}
												>
													{service.status ===
													'pending'
														? 'PENDENTE'
														: service.status ===
														  'in_progress'
														? 'EM ANDAMENTO'
														: service.status ===
														  'completed'
														? 'CONCLUÍDO'
														: service.status.toUpperCase()}
												</span>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default ProjectTable;
